import {
  DownloadHandle,
  DownloadProgress,
  FillOptions,
} from './videoDownload.types';

const CACHE_NAME = 'video-cache';

/**
 * Web implementation of video download.
 *
 * Pre-caching requires the CDN to serve CORS headers (Access-Control-Allow-Origin).
 * If CORS is available, the video is downloaded via fetch() + Cache API with progress
 * tracking, and the service worker serves it from cache on subsequent requests.
 *
 * If CORS is not available (fetch fails), we fall back to resolving immediately with
 * the original URL so the video streams directly. This keeps the flow working while
 * CORS headers are being configured on the CDN.
 */
export function downloadVideo(
  url: string,
  onProgress?: (progress: DownloadProgress) => void,
): DownloadHandle {
  const controller = new AbortController();
  let cancelled = false;

  const promise = (async () => {
    let response: Response;
    try {
      response = await fetch(url, { signal: controller.signal });
    } catch {
      // CORS or network error — fall back to streaming the original URL
      console.warn(
        '[videoDownload] fetch failed (likely CORS), falling back to streaming:',
        url,
      );
      onProgress?.({
        totalBytes: 0,
        downloadedBytes: 0,
        progress: 100,
      });
      return { uri: url, streamed: true };
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const clone = response.clone();

    // Read the original stream for progress tracking — chunks are discarded
    const contentLength = Number(response.headers.get('Content-Length')) || 0;
    const reader = response.body?.getReader();

    if (reader) {
      let receivedLength = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        receivedLength += value.length;
        if (contentLength > 0 && onProgress) {
          onProgress({
            totalBytes: contentLength,
            downloadedBytes: receivedLength,
            progress: Math.round((receivedLength / contentLength) * 100),
          });
        }
      }
    }

    // Cache the cloned response — if this fails, fall back to streaming
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(url, clone);
    } catch (err) {
      console.warn('[videoDownload] Cache.put failed, falling back to streaming:', err);
      return { uri: url, streamed: true };
    }

    // Return the original URL — the service worker intercepts and serves from cache
    return { uri: url, streamed: false };
  })();

  return {
    promise,
    cancel: () => {
      if (!cancelled) {
        cancelled = true;
        controller.abort();
      }
    },
  };
}

/**
 * Web returns null: there's no meaningful pre-download capacity check, and a
 * full cache already degrades gracefully to streaming (see downloadVideo). A
 * null result tells callers to skip the space check entirely.
 */
export async function getFreeDiskSpace(): Promise<number | null> {
  return null;
}

/** Web can't report total disk capacity. */
export async function getTotalDiskSpace(): Promise<number | null> {
  return null;
}

/** DEBUG: disk filling is only meaningful on native platforms. */
export async function fillRemainingSpace(
  _options?: FillOptions,
): Promise<number> {
  throw new Error('Disk fill is only supported on native platforms');
}

/** DEBUG: no-op on web (no filler file exists). */
export async function removeFillerFile(): Promise<void> {}

export async function getCachedVideoUri(url: string): Promise<string | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const match = await cache.match(url);
    // Service worker intercepts fetches for this URL and serves the cached
    // response, so the playback URI is the original URL itself.
    return match ? url : null;
  } catch {
    return null;
  }
}

export async function deleteCachedVideo(url: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  const cache = await caches.open(CACHE_NAME);
  await cache.delete(url);
}

export async function clearAllCachedVideos(): Promise<void> {
  if (typeof caches === 'undefined') {
    console.log('[videoDownload] clearAllCachedVideos: Cache API unavailable');
    return;
  }
  const deleted = await caches.delete(CACHE_NAME);
  console.log(
    `[videoDownload] clearAllCachedVideos: ${CACHE_NAME} ${deleted ? 'deleted' : 'not present'}`,
  );
}

export async function getCachedVideosSize(): Promise<number> {
  if (typeof caches === 'undefined') return 0;
  let total = 0;
  let count = 0;
  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();
    for (const request of requests) {
      const response = await cache.match(request);
      if (!response) continue;
      count += 1;
      const contentLength = Number(response.headers.get('Content-Length'));
      if (contentLength > 0) {
        total += contentLength;
      } else {
        const blob = await response.blob();
        total += blob.size;
      }
    }
  } catch (err) {
    console.warn('[videoDownload] getCachedVideosSize failed', err);
    return 0;
  }
  console.log(
    `[videoDownload] getCachedVideosSize: ${count} entry/entries, ${total} bytes`,
  );
  return total;
}
